import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useEffect, useState } from 'react';
import { t } from '../../../../../types/translation/Translator';
import { TranslationContext } from '../../../../../context/TranslationContext';
import {
  TranslationMutations,
  UpdateTranslationRecordResponse,
  UpdateTranslationRecordVariables,
} from '../../../../../graphql/translations/translation.graphql';
import { emptyTranslationRecord, TranslationRecord, TranslationUtils } from '../../../../../types/translation/common';
import { Translation, TranslationLanguage } from '../../../../../types/translation/translation';
import { translationsManager } from '../../../../../types/translation/Translator';
import { ActionModal } from '../../../../components/Modal';
import useMutation from '../../../../../hooks/useMutation';
import { TextInput } from '../../../../components/TextInput';
import { CancelButton, SaveButton } from '../../../../components/Button';
import { Feedback } from '../../../../components/Feedback/feedback';
import { ISO_LanguageCode } from '../../../../../types/translation/localization.utils';

interface TranslationRecordModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  translation: Translation;
  translationRecord: TranslationRecord;
  translationKey: TranslationKey;
}

export default function TranslationRecordModal({
  open,
  setOpen,
  translation,
  translationRecord,
  translationKey,
}: TranslationRecordModalProps) {
  const [record, setRecord] = useState(translationRecord);

  const { translations, setTranslations } = useContext(TranslationContext);

  const englishRecord =
    translations.get(TranslationLanguage.en)?.translations[translationKey]?.toRecord() || emptyTranslationRecord;

  useEffect(() => {
    setRecord(translationRecord);
  }, [translationRecord]);

  const [update, { loading }] = useMutation<UpdateTranslationRecordResponse, UpdateTranslationRecordVariables>(
    TranslationMutations.updateRecord,
    {
      variables: {
        key: translationKey,
        language: translation.language,
        record: record,
      },
      onCompleted: response => {
        const translation = Translation.fromJson(
          response.updateTranslationRecord.language,
          response.updateTranslationRecord.translations,
        );
        setTranslations(new Map(translations).set(response.updateTranslationRecord.language, translation));
        translationsManager.setTranslation(translation);
        setOpen(false);
      },
    },
  );

  const hasVariables =
    translation.language === ISO_LanguageCode.EN ||
    (TranslationUtils.checkVariables(englishRecord.singular.lower, record.singular.lower) &&
      TranslationUtils.checkVariables(englishRecord.plural?.lower || '', record.plural?.lower || ''));

  const hasRequired =
    !(englishRecord.singular.lower && !record.singular.lower) &&
    !(englishRecord.plural?.lower && !record.plural?.lower);

  const canSave = hasVariables && hasRequired;

  return (
    <ActionModal
      title={t(TranslationKey.translationRecord).singular.upper}
      setOpen={setOpen}
      open={open}
      footer={
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <SaveButton onClick={() => update()} loading={loading} disabled={!canSave} />
        </div>
      }
    >
      <div className='grid grid-cols-2 gap-4'>
        <div className='col-span-2 grid grid-cols-2'>
          <TextInput
            disabled
            label={t(TranslationKey.key).singular.label}
            value={translationKey}
            onChange={() => null}
          />
        </div>

        <TextInput
          disabled
          label={t(TranslationKey.englishSingular).singular.label}
          value={englishRecord.singular?.lower}
          onChange={() => null}
        />

        <TextInput
          mandatory
          label={t(TranslationKey.singular).singular.label}
          value={record.singular.lower}
          onChange={value => setRecord({ ...record, singular: { lower: value } })}
          feedback={value => {
            if (!value) return Feedback.mandatory(value);
            if (
              translation.language !== ISO_LanguageCode.EN &&
              !TranslationUtils.checkVariables(englishRecord.singular.lower, value)
            )
              return {
                type: Feedback.Type.error,
                text: 'Variables like this: "${VALUE}" must be present in your translation.',
              };
          }}
        />

        <TextInput
          disabled
          label={t(TranslationKey.englishPlural).singular.label}
          value={englishRecord.plural?.lower}
          onChange={() => null}
        />

        <TextInput
          mandatory={!!englishRecord.plural?.lower}
          label={t(TranslationKey.plural).singular.label}
          value={record.plural?.lower}
          disabled={!englishRecord.plural?.lower && translation.language !== TranslationLanguage.en}
          feedback={value => {
            if (!value) return Feedback.mandatory(value);
            if (!englishRecord.plural?.lower) return;
            if (!TranslationUtils.checkVariables(englishRecord.plural.lower, value)) {
              return {
                type: Feedback.Type.error,
                text: 'Variables like this: "${VALUE}" must be present in your translation.',
              };
            }
          }}
          onChange={value => {
            if (!record.plural) record.plural = { lower: value };
            else record.plural.lower = value;
            setRecord({ ...record });
          }}
        />
      </div>
    </ActionModal>
  );
}
